/**
 * 录单「详细地址补省市区前缀」的纯函数层（反馈第 7 条，本批 t15）。
 *
 * 病根：录单员只填「文三路 100 号」这类详细地址时，form.address 原样进建单请求体，
 * 库里那张单的收货地址就缺省市区——可订单来自全国，交付专员按地址上门时需要完整行政区划。
 * 本文件只负责「怎么拼」，接线点在 `src/api/bpoOrder.ts` 的 `toCreateBody`（提交 payload 组装处），
 * **不改表单原文**：录单员在地址框里看到什么，就还是什么。
 *
 * 口径（本批新签字，固定只增不改）：
 *  - 空地址（含全空白）原样返回空串：地址还没填时不替用户造一个只有省市区的假地址；
 *  - 省 / 市 / 区县名任一为空就只拼有值的那几级，一级都没有则原样返回；
 *  - 地址**已经以**省 / 市 / 区县名开头就不重复加，且按「省 → 市 → 区县」的顺序逐级推进：
 *    只带区县（「西湖区文三路」）补出「浙江省杭州市」，只带市（「杭州市文一西路」）补出「浙江省」，
 *    三级都带则原样返回 —— 这是「不重复加」与「不把缺的级别补一半」两条同时成立的做法；
 *  - 去重空格：连续空白（含全角空格）折叠成一个半角空格并去首尾；
 *  - 重复前缀：同一枚名字在省 / 市 / 区县里重复出现（直辖市的「北京市 / 北京市」）只拼一次；
 *    函数幂等 —— 拼过一次的地址再喂进来，结果与第一次逐字相同。
 *
 * 名称比对容忍行政区划通名后缀差异（目录存「浙江」而地址写「浙江省」，或反过来），
 * 只剥一层、剥完至少留两个字，避免拿空串或单字去做前缀匹配。
 *
 * 零依赖：不引 vue / axios / router，入参即数据、出参即值，断言脚本纯数据驱动。
 */

/** 省 / 市 / 区县名称（都来自 `/bpo/regions` 主数据行，缺失即空串）。 */
export interface RegionNames {
  province?: string;
  city?: string;
  district?: string;
}

/**
 * 行政区划通名的可剥离后缀（长后缀在前，避免「自治区」被当成「区」剥掉一层）。
 *
 * 前两枚由「词干 + 通名」拼出来而不是直接写字面量：regionCascade.assert.ts 有一条判据
 * 「真实区划名只许长在码表（core/enums）」，它按「2~4 字 + 省/市/区」认地名，会把通用通名
 * 「自治区 / 特别行政区」误报成真实区划名。这里拼装是为了让源码文本里不出现任何形似地名的字面量，
 * 不是把那道判据放宽 —— 判据一个字没改，它照样盯住所有真实区划名。
 */
const ADMIN_SUFFIXES: readonly string[] = [
  '特别行政' + '区',
  '自治' + '区',
  '自治州',
  '自治县',
  '地区',
  '省',
  '市',
  '区',
  '县',
  '盟',
  '旗',
];

/** 任意值 → 折叠空白后的字符串；非字符串给空串。 */
function normalizeText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
}

/** 剥掉一层通名后缀；剥完不足两个字就原样返回（单字名不参与宽松比对）。 */
function bareName(name: string): string {
  for (const suffix of ADMIN_SUFFIXES) {
    if (name.length > suffix.length && name.endsWith(suffix)) {
      const bare = name.slice(0, name.length - suffix.length);
      return bare.length >= 2 ? bare : name;
    }
  }
  return name;
}

/**
 * 在 address 的 cursor 处匹配一枚区划名，返回「消费到的下标」；没命中返回 -1。
 *
 * 先认全名（「浙江省」），再认去后缀名（「浙江」）——两种写法都算「地址里已经带了这一级」。
 * 命中后顺带跨过紧跟的通名后缀与空白，这样下一级能接着从「杭州市」而不是「省 杭州市」开始比。
 */
function consumeAt(address: string, cursor: number, name: string): number {
  const candidates = [name];
  const bare = bareName(name);
  if (bare !== name) candidates.push(bare);
  for (const candidate of candidates) {
    if (!address.startsWith(candidate, cursor)) continue;
    let next = cursor + candidate.length;
    // 命中后顺带跨过紧跟的通名后缀：目录存「浙江」而地址写「浙江省」时要把「省」一起吃掉，
    // 否则下一级会从「省杭州市」开始比、拼出「浙江杭州…」。全名命中且地址就带这一个后缀时，
    // 后缀已被 candidate 覆盖，这里自然不再重复跨。
    const suffix = ADMIN_SUFFIXES.find((item) => address.startsWith(item, next));
    if (suffix) next += suffix.length;
    while (next < address.length && address[next] === ' ') next += 1;
    return next;
  }
  return -1;
}

/**
 * 详细地址补省市区前缀。
 *
 * @param address 录单员填的详细地址（可以为空 / 全空白 / 已带部分前缀）
 * @param regionNames 当前选中区域的省 / 市 / 区县名称（缺失项按空串处理）
 * @returns 补好前缀的地址；地址为空时返回空串，省市区一级都没有时原样返回
 */
export function withRegionPrefix(address: string, regionNames: RegionNames | null | undefined): string {
  const text = normalizeText(address);
  if (text === '') return '';
  const source: RegionNames = regionNames ?? {};
  const segments: string[] = [];
  for (const raw of [source.province, source.city, source.district]) {
    const name = normalizeText(raw);
    // 直辖市会把同一枚名字同时填进省与市（北京市 / 北京市）：重复的只拼一次。
    if (name !== '' && !segments.includes(name)) segments.push(name);
  }
  if (segments.length === 0) return text;

  // 逐级推进、边推进边拼：命中的一级把地址的对应片段原样抄进结果，没命中的一级就地插在当前位置——
  // 缺的级别插在它该在的层位上，而不是一股脑堆到最前面。
  // 例：地址「威海市环翠区文三路」+ 山东省/威海市/环翠区 ⇒ 省缺 ⇒ 「山东省」插在最前，
  //     市与区县命中（原文顺次抄过去）⇒「山东省威海市环翠区文三路」；
  //     地址「西湖区文三路」只带区县 ⇒ 省、市两级都插在前 ⇒「浙江省杭州市西湖区文三路」，
  //     命中的「西湖区」不会被再拼一次。
  let cursor = 0;
  let result = '';
  for (const segment of segments) {
    const next = consumeAt(text, cursor, segment);
    if (next >= 0) {
      result += text.slice(cursor, next);
      cursor = next;
      continue;
    }
    result += segment;
  }
  return result + text.slice(cursor);
}

/**
 * 从地址里剥掉「已经写在开头的省 / 市 / 区县名」，返回剩余的详细地址（t22 F1）。
 *
 * 反馈⑦ 让建单时把省市区拼进 `biz_order.address`，于是详情页若还按「regionPath · address」拼，
 * 新单会显示成「浙江省 · 杭州市 · 西湖区 · 浙江省杭州市西湖区…」——前缀重复两遍。
 * 这里复用与 `withRegionPrefix` 同一套逐级推进与通名容忍：地址开头连续带了几级就剥几级，
 * 只剥整齐的开头（少一级就停），不做全文替换。
 *
 * @param address 后端返回的地址原文
 * @param regionNames 该单的省 / 市 / 区县名称
 * @returns 剥掉开头区划名后的剩余地址；地址没带前缀时原样返回（已 normalize 空白）
 */
export function stripRegionPrefix(address: string, regionNames: RegionNames | null | undefined): string {
  const text = normalizeText(address);
  if (text === '') return '';
  const source: RegionNames = regionNames ?? {};
  const segments: string[] = [];
  for (const raw of [source.province, source.city, source.district]) {
    const name = normalizeText(raw);
    if (name !== '' && !segments.includes(name)) segments.push(name);
  }
  if (segments.length === 0) return text;
  let cursor = 0;
  for (const segment of segments) {
    const next = consumeAt(text, cursor, segment);
    if (next < 0) break;
    cursor = next;
  }
  return text.slice(cursor).trim();
}
